/**
 * Loads and validates unit configuration from JSON
 */

import type {
  UnitsConfigFile,
  UnitConfig,
  ValidationResult,
  ValidationError,
  BaseStatsConfig,
} from './configTypes';
import { UnitDefinition, UnitMeshType } from '@/units/UnitDefinitions';

// Valid values for validation
const VALID_MESH_TYPES = ['soldier', 'beast', 'mage', 'assassin', 'golem', 'archer'] as const;
const VALID_TRAITS = [
  'tank',
  'ranger',
  'swarm',
  'mage',
  'assassin',
  'berserker',
  'support',
  'human',
  'beast',
  'elemental',
  'mechanical',
];

/**
 * Validates the entire units config file
 */
export function validateUnitsConfig(config: unknown): ValidationResult {
  const errors: ValidationError[] = [];

  if (!config || typeof config !== 'object') {
    return { valid: false, errors: [{ path: '', message: 'Config must be an object' }] };
  }

  const cfg = config as Record<string, unknown>;

  // Check version
  if (typeof cfg.version !== 'string') {
    errors.push({ path: 'version', message: 'Version must be a string' });
  } else if (!/^\d+\.\d+\.\d+$/.test(cfg.version)) {
    errors.push({ path: 'version', message: 'Version must be semver format (e.g., "1.0.0")' });
  }

  // Check units object
  if (!cfg.units || typeof cfg.units !== 'object') {
    errors.push({ path: 'units', message: 'Units must be an object' });
    return { valid: false, errors };
  }

  const units = cfg.units as Record<string, unknown>;

  // Validate each unit
  for (const [unitId, unitData] of Object.entries(units)) {
    const unitErrors = validateUnit(unitId, unitData);
    errors.push(...unitErrors);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validates a single unit definition
 */
function validateUnit(unitId: string, unit: unknown): ValidationError[] {
  const errors: ValidationError[] = [];
  const path = `units.${unitId}`;

  if (!unit || typeof unit !== 'object') {
    return [{ path, message: 'Unit must be an object' }];
  }

  const u = unit as Record<string, unknown>;

  // Required string fields
  if (typeof u.id !== 'string' || u.id.length === 0) {
    errors.push({ path: `${path}.id`, message: 'ID is required and must be a non-empty string' });
  } else if (u.id !== unitId) {
    errors.push({ path: `${path}.id`, message: `ID "${u.id}" must match key "${unitId}"` });
  }

  if (typeof u.name !== 'string' || u.name.length === 0) {
    errors.push({ path: `${path}.name`, message: 'Name is required' });
  }

  // Tier validation
  if (typeof u.tier !== 'number' || u.tier < 1 || u.tier > 5 || !Number.isInteger(u.tier)) {
    errors.push({ path: `${path}.tier`, message: 'Tier must be an integer between 1 and 5' });
  }

  // Traits validation
  if (!Array.isArray(u.traits) || u.traits.length === 0) {
    errors.push({ path: `${path}.traits`, message: 'Traits must be a non-empty array' });
  } else {
    for (const trait of u.traits) {
      if (!VALID_TRAITS.includes(trait as string)) {
        errors.push({
          path: `${path}.traits`,
          message: `Invalid trait "${trait}". Valid: ${VALID_TRAITS.join(', ')}`,
        });
      }
    }
  }

  // BaseStats validation
  if (!u.baseStats || typeof u.baseStats !== 'object') {
    errors.push({ path: `${path}.baseStats`, message: 'baseStats is required' });
  } else {
    errors.push(...validateBaseStats(path, u.baseStats as Record<string, unknown>));
  }

  // Visual validation
  if (!u.visual || typeof u.visual !== 'object') {
    errors.push({ path: `${path}.visual`, message: 'visual is required' });
  } else {
    errors.push(...validateVisual(path, u.visual as Record<string, unknown>));
  }

  // Abilities validation (optional)
  if (u.abilities !== undefined) {
    if (!Array.isArray(u.abilities)) {
      errors.push({ path: `${path}.abilities`, message: 'abilities must be an array' });
    } else {
      for (let i = 0; i < u.abilities.length; i++) {
        const ability = u.abilities[i] as Record<string, unknown>;
        if (typeof ability.abilityId !== 'string') {
          errors.push({
            path: `${path}.abilities[${i}].abilityId`,
            message: 'abilityId is required',
          });
        }
        if (
          ability.unlockStar !== undefined &&
          (typeof ability.unlockStar !== 'number' ||
            ability.unlockStar < 1 ||
            ability.unlockStar > 4)
        ) {
          errors.push({
            path: `${path}.abilities[${i}].unlockStar`,
            message: 'unlockStar must be 1-4',
          });
        }
      }
    }
  }

  return errors;
}

/**
 * Validates baseStats object
 */
function validateBaseStats(parentPath: string, stats: Record<string, unknown>): ValidationError[] {
  const errors: ValidationError[] = [];
  const path = `${parentPath}.baseStats`;

  const requiredFields: { name: keyof BaseStatsConfig; min?: number; max?: number }[] = [
    { name: 'maxHealth', min: 1 },
    { name: 'attack', min: 0 },
    { name: 'defense', min: 0 },
    { name: 'speed', min: 0 },
    { name: 'attackRange', min: 1, max: 6 },
    { name: 'attackSpeed', min: 0.1, max: 5.0 },
  ];

  for (const field of requiredFields) {
    const value = stats[field.name];
    if (typeof value !== 'number') {
      errors.push({ path: `${path}.${field.name}`, message: `${field.name} must be a number` });
    } else {
      if (field.min !== undefined && value < field.min) {
        errors.push({
          path: `${path}.${field.name}`,
          message: `${field.name} must be >= ${field.min}`,
        });
      }
      if (field.max !== undefined && value > field.max) {
        errors.push({
          path: `${path}.${field.name}`,
          message: `${field.name} must be <= ${field.max}`,
        });
      }
    }
  }

  // Optional fields
  if (stats.abilityPower !== undefined && typeof stats.abilityPower !== 'number') {
    errors.push({ path: `${path}.abilityPower`, message: 'abilityPower must be a number' });
  }

  if (stats.critChance !== undefined) {
    if (
      typeof stats.critChance !== 'number' ||
      stats.critChance < 0 ||
      stats.critChance > 1
    ) {
      errors.push({ path: `${path}.critChance`, message: 'critChance must be 0-1' });
    }
  }

  if (stats.critMultiplier !== undefined) {
    if (typeof stats.critMultiplier !== 'number' || stats.critMultiplier < 1) {
      errors.push({ path: `${path}.critMultiplier`, message: 'critMultiplier must be >= 1' });
    }
  }

  return errors;
}

/**
 * Validates visual config object
 */
function validateVisual(parentPath: string, visual: Record<string, unknown>): ValidationError[] {
  const errors: ValidationError[] = [];
  const path = `${parentPath}.visual`;

  // Color is required
  if (typeof visual.color !== 'string') {
    errors.push({ path: `${path}.color`, message: 'color is required' });
  } else if (!/^#[0-9A-Fa-f]{6}$/.test(visual.color)) {
    errors.push({ path: `${path}.color`, message: 'color must be hex format (#RRGGBB)' });
  }

  // Either meshType or model is required
  const hasMeshType = visual.meshType !== undefined;
  const hasModel = visual.model !== undefined;

  if (!hasMeshType && !hasModel) {
    errors.push({ path, message: 'Either meshType or model is required' });
  }

  if (hasMeshType && !VALID_MESH_TYPES.includes(visual.meshType as typeof VALID_MESH_TYPES[number])) {
    errors.push({
      path: `${path}.meshType`,
      message: `Invalid meshType. Valid: ${VALID_MESH_TYPES.join(', ')}`,
    });
  }

  if (hasModel && typeof visual.model !== 'string') {
    errors.push({ path: `${path}.model`, message: 'model must be a string path' });
  }

  if (visual.scale !== undefined) {
    if (typeof visual.scale !== 'number' || visual.scale < 0.1 || visual.scale > 3.0) {
      errors.push({ path: `${path}.scale`, message: 'scale must be 0.1-3.0' });
    }
  }

  return errors;
}

/**
 * Converts hex color string to number
 */
function hexColorToNumber(hex: string): number {
  return parseInt(hex.slice(1), 16);
}

/**
 * Maps meshType string to UnitMeshType enum
 */
function toMeshType(meshType: string): UnitMeshType {
  const mapping: Record<string, UnitMeshType> = {
    soldier: UnitMeshType.Soldier,
    beast: UnitMeshType.Beast,
    mage: UnitMeshType.Mage,
    assassin: UnitMeshType.Assassin,
    golem: UnitMeshType.Golem,
    archer: UnitMeshType.Archer,
  };
  return mapping[meshType] ?? UnitMeshType.Soldier;
}

/**
 * Converts a UnitConfig to the existing UnitDefinition format
 */
export function convertToUnitDefinition(config: UnitConfig): UnitDefinition {
  return {
    unitType: config.id,
    name: config.name,
    baseStats: {
      attack: config.baseStats.attack,
      defense: config.baseStats.defense,
      speed: config.baseStats.speed,
      abilityPower: config.baseStats.abilityPower ?? 0,
      maxHealth: config.baseStats.maxHealth,
      attackRange: config.baseStats.attackRange,
      attackSpeed: config.baseStats.attackSpeed,
    },
    traits: config.traits,
    tier: config.tier,
    meshType: config.visual.meshType ? toMeshType(config.visual.meshType) : UnitMeshType.Soldier,
    color: hexColorToNumber(config.visual.color),
    model: config.visual.model,
    scale: config.visual.scale,
  };
}

/**
 * Loads units config and converts to UnitDefinition record
 */
export function loadUnitsFromConfig(config: UnitsConfigFile): Record<string, UnitDefinition> {
  const result: Record<string, UnitDefinition> = {};

  for (const [id, unitConfig] of Object.entries(config.units)) {
    result[id] = convertToUnitDefinition(unitConfig);
  }

  return result;
}

/**
 * Loads and validates units from a JSON object
 * Throws if validation fails
 */
export function loadAndValidateUnits(json: unknown): Record<string, UnitDefinition> {
  const validation = validateUnitsConfig(json);

  if (!validation.valid) {
    const errorMessages = validation.errors.map((e) => `  ${e.path}: ${e.message}`).join('\n');
    throw new Error(`Unit config validation failed:\n${errorMessages}`);
  }

  return loadUnitsFromConfig(json as UnitsConfigFile);
}

/**
 * Fetches and loads units from a URL
 */
export async function fetchUnitsConfig(url: string): Promise<Record<string, UnitDefinition>> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch units config: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();
  return loadAndValidateUnits(json);
}
